import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Upload, FileSpreadsheet, X } from "lucide-react";

// Human-readable file size for the selected-file chip.
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Reusable .xlsx/.xls drag-and-drop dropzone with a client-side file-type guard.
// Once a file is chosen it collapses to a chip (icon + name + size + clear button).
// Shared by the File-import and Agreement-import steps.
export function Dropzone({
  file,
  onFile,
  onClear,
  disabled,
}: {
  file: File | null;
  onFile: (file: File) => void;
  onClear?: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = useCallback(
    (f: File | undefined | null) => {
      if (!f) return;
      if (!/\.(xlsx|xls)$/i.test(f.name)) {
        setError(t("import.dropzone.invalidType"));
        return;
      }
      setError(null);
      onFile(f);
    },
    [onFile, t],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (!disabled) accept(e.dataTransfer.files?.[0]);
    },
    [disabled, accept],
  );

  // Selected-file chip.
  if (file) {
    return (
      <div className="mb-5 flex items-center gap-3 rounded-card border border-grey-200 bg-white px-4 py-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-green-bg text-green">
          <FileSpreadsheet size={20} strokeWidth={1.8} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-medium text-grey-800">{file.name}</div>
          <div className="text-[12px] text-grey-400 tnum">{formatBytes(file.size)}</div>
        </div>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            disabled={disabled}
            aria-label={t("import.dropzone.clear")}
            className="shrink-0 rounded-control p-1.5 text-grey-400 transition-colors hover:bg-grey-100 hover:text-grey-600 disabled:opacity-50"
          >
            <X size={16} strokeWidth={2} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mb-5">
      <div
        role="button"
        tabIndex={0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && !disabled && inputRef.current?.click()
        }
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed py-10 transition-colors ${
          dragOver
            ? "border-green bg-green-bg-soft"
            : "border-grey-200 bg-grey-25 hover:border-green"
        } ${disabled ? "pointer-events-none opacity-60" : ""}`}
      >
        <Upload size={28} className="text-green" strokeWidth={1.6} />
        <p className="text-[14px] font-medium text-grey-700">{t("import.dropzone.label")}</p>
        <p className="text-[12px] text-grey-400">{t("import.dropzone.hint")}</p>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={(e) => accept(e.target.files?.[0])}
        />
      </div>
      {error && <p className="mt-2 text-[12px] text-red">{error}</p>}
    </div>
  );
}
